import { getSettingsPageUrl, NocfoPlanSource } from '@constants'
import { isBillableBusiness, PermissionSet, useBusinessContext, usePermission } from '@context'
import { useTheme } from '@hooks'
import { fetchIntegrationRenewInfo, isBusinessBillingLocked } from '@query'
import { usePaymentMethodStatus } from '@root/query/hooks'
import { PlanManagementPanel } from '@pages/SettingsPage/constants'
import { isLucaPaygBlockedByMissingPaymentMethod } from '@utils/lucaPaygBilling'
import { useLucaUsageVisibility } from '@utils/lucaUsageVisibility'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaExclamation, FaExclamationTriangle } from 'react-icons/fa'

export type SettingsMenuNotification = {
  icon: React.ReactNode
  message: string
}

export const useSettingsMenuNotification = ({ enabled = true }: { enabled?: boolean } = {}): {
  notification: SettingsMenuNotification | undefined
  settingsPath: string
} => {
  const { slug, data: business } = useBusinessContext()
  const theme = useTheme()
  const [t] = useTranslation()
  const canManageBilling = usePermission(PermissionSet.ADMIN)
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)
  const isBillingLocked = isBusinessBillingLocked(business)
  const isManualPlan = business?.subscription_source === NocfoPlanSource.MANUAL
  const isAgencyCustomer = business?.subscription_source === NocfoPlanSource.AGENCY
  const isNonDirectBillingCustomer = isManualPlan || isAgencyCustomer
  const stripePaygCheckoutEnabled = Boolean(business?.stripe_payg_checkout_enabled)
  // Holvi settings are integration-admin only; only surface the stale badge to those users.
  const holviStale = canManageIntegrations && Boolean(business?.holvi_bookkeeping_api_staled)

  const { data: integrationRenewData } = useQuery({
    queryKey: [slug, 'integration_renew_info'],
    queryFn: () => fetchIntegrationRenewInfo({ slug }),
    enabled: enabled && canManageIntegrations && Boolean(slug) && !isBillingLocked
  })

  const renewPending = integrationRenewData?.renew_pending
  const renewPendingSoon =
    !integrationRenewData?.renew_pending && integrationRenewData?.renew_pending_soon
  const hasBankWarning = Boolean(renewPending || renewPendingSoon)

  // Stripe payment-method endpoints require company admin.
  const { data: paymentMethodStatus } = usePaymentMethodStatus({
    slug,
    enabled:
      enabled &&
      canManageBilling &&
      stripePaygCheckoutEnabled &&
      !isBillingLocked &&
      isBillableBusiness(business) &&
      !isNonDirectBillingCustomer
  })

  const isUsageBillingPaymentAtRisk = paymentMethodStatus?.usage_billing_payment_at_risk ?? false
  const paymentAtRiskTooltip = t('settings.billing.usageBillingPaymentAtRisk.sidebarTooltip')
  const { showLucaUsage } = useLucaUsageVisibility()
  const isLucaPaygWithoutPaymentMethod =
    showLucaUsage &&
    canManageBilling &&
    !isBillingLocked &&
    isLucaPaygBlockedByMissingPaymentMethod(business)

  const notification = (() => {
    if (holviStale) {
      const holviTooltip = t('settings.holvi.expiredIconTooltip')
      return {
        icon: <FaExclamation color={theme.colors.error} />,
        message: isUsageBillingPaymentAtRisk
          ? `${holviTooltip} ${paymentAtRiskTooltip}`
          : holviTooltip
      }
    }

    if (renewPending) {
      const bankTooltip = t('settings.integrations.bank.expiredIconTooltip')
      return {
        icon: <FaExclamation color={theme.colors.error} />,
        message: isUsageBillingPaymentAtRisk
          ? `${bankTooltip} ${paymentAtRiskTooltip}`
          : bankTooltip
      }
    }

    if (renewPendingSoon) {
      const bankTooltip = t('settings.integrations.bank.goingToExpireIconTooltip')
      return {
        icon: <FaExclamationTriangle color={theme.colors.warning} />,
        message: isUsageBillingPaymentAtRisk
          ? `${bankTooltip} ${paymentAtRiskTooltip}`
          : bankTooltip
      }
    }

    if (isUsageBillingPaymentAtRisk) {
      return {
        icon: <FaExclamationTriangle color={theme.colors.warning} />,
        message: paymentAtRiskTooltip
      }
    }

    if (isLucaPaygWithoutPaymentMethod) {
      return {
        icon: <FaExclamationTriangle color={theme.colors.warning} />,
        message: t('settings.billing.lucaUsage.paygMissingPaymentMethod.title')
      }
    }

    return undefined
  })()

  const settingsPath = getSettingsPageUrl(slug, {
    page: holviStale
      ? 'holvi'
      : hasBankWarning
      ? 'integrations'
      : isUsageBillingPaymentAtRisk
      ? 'plan-management'
      : isLucaPaygWithoutPaymentMethod
      ? 'luca-usage'
      : undefined,
    panel:
      !holviStale && !hasBankWarning && isUsageBillingPaymentAtRisk
        ? PlanManagementPanel.PAYMENT_METHOD
        : undefined
  })

  return { notification, settingsPath }
}
